import { useSdk, isOidcBasedProvider } from "@fondationcss/sdk";
import type { ExamRequest, ExamResponse, ExamsResponse } from "./dto";
import type { ConsolidatedViewResponse } from "./dto/consolidated-view-response";
import type { CumulativeHoursResponse } from "./dto/cumulative-hours-response";
import type { ExamPresenceReportRequest } from "./dto/exam-presence-report-request";
import type { ImportResultDto } from "./dto/export-import-dto";
import type { FrequentationResponse, FrequentationsResponse, PagedFrequentationsResponse } from "./dto/frequentation-response";
import type { FrequentationModuleResultsResponse } from "./dto/frequentation-module-results-response";
import type { LearningModuleRequest } from "./dto/learning-module-request";
import type { LearningModuleResponse } from "./dto/learning-module-response";
import type { LearningModulesResponse } from "./dto/learning-modules-response";
import type { LearningProgramRequest } from "./dto/learning-program-request";
import type { LearningProgramResponse } from "./dto/learning-program-response";
import type { LearningProgramsResponse } from "./dto/learning-programs-response";
import type { ModulePlanResponse, ModulePlansBatchResponse } from "./dto/module-plan-response";
import type { EffectivePlanResponse } from "./dto/effective-plan-response";
import type { TeacherDailyReportResponse } from "./dto/teacher-daily-report-response";
import type { PagedGridQuery } from "@fondationcss/ui";
import type { PresenceRequest } from "./dto/presence-request";
import type { PresenceResponse } from "./dto/presence-response";
import type { PresencesByStudentResponse } from "./dto/presences-by-student-response";
import type { StudentJourneyResponse } from "./dto/student-journey-response";
import type { PagedStudentsResponse } from "./dto/student-response";
import type { PagedTeachersResponse, TeacherResponse, TeachersResponse } from "./dto/teacher-response";
import type { AteEnterprise, AteEnterpriseContact } from "./model/ate-enterprise";
import type { AteHAC, AteHACEntry } from "./model/ate-hac";
import type { AteActivityAssessment, AteWorkPlacementActivity, AteWorkPlacementActivityNote } from "./model/ate-journal";
import type { AteQuestionnaire, AteQuestionnaireItem } from "./model/ate-questionnaire";
import type { AteActivityTemplate, AteAssessmentRatingGroup, AteHACTemplate, AteQuestionnaireItemTemplate, AteQuestionnaireTemplate } from "./model/ate-reference-data";
import type { AteWorkPlacementVisit, AteWorkPlacementVisitNote } from "./model/ate-visit";
import type { AteWorkPlacement, AteWorkPlacementNote } from "./model/ate-work-placement";
import type { Exam } from "./model/exam";
import type { ExamListItem } from "./model/exam-list-item";
import type { FrequentationNote } from './model/frequentation-note';
import type { CreateFrequentationNoteTypeDefinitionRequest, FrequentationNoteTypeDefinition, FrequentationNoteTypeDefinitionsResponse, UpdateFrequentationNoteTypeDefinitionRequest } from './model/frequentation-note-type-definition';
import type { CreateFrequentationTagRequest, FrequentationTag, FrequentationTagAssignmentsResponse, FrequentationTagsResponse, FrequentationTagValueRequest, UpdateFrequentationTagRequest } from './model/frequentation-tag';
import type {
    ActiveGroupsResponse,
    CreateGroupRequest,
    GroupModuleSequencesResponse,
    GroupResponse,
    GroupsResponse,
    UpdateGroupModuleSequencesRequest,
    UpdateGroupRequest
} from "./model/group";
import type { GroupDashboardStats } from "./model/group-dashboard-stats";
import type { GroupMemberResponse, GroupMembersByProgramResponse, GroupMembersResponse } from "./model/group-membership";
import type {
    GroupModuleTeachersByProgramResponse,
    GroupModuleTeachersResponse,
    UpdateGroupModuleTeachersRequest
} from "./model/group-module-teacher";
import type { GroupStatsResponse } from "./model/group-stats";
import type { CreateLocalRequest, Local, UpdateLocalRequest } from './model/local';
import type { CreateResourceRequest, Resource, SetResourceProgramsRequest, UpdateResourceRequest } from './model/resource';
import type {
    CreatePlanningRequest,
    PlanningResponse,
    PlanningsResponse,
    UpdatePlanningRequest
} from "./model/planning";
import type {
    CreatePlanningExceptionRequest,
    PlanningExceptionResponse,
    PlanningExceptionsResponse,
    UpdatePlanningExceptionRequest
} from "./model/planning-exception";
import type { Presence } from "./model/presence";
import type { CreatePresenceTypeDefinitionRequest, PresenceTypeDefinition, PresenceTypeDefinitionsResponse, UpdatePresenceTypeDefinitionRequest } from './model/presence-type-definition';
import type {
    ProgramModuleSequencesResponse,
    UpdateProgramModuleSequencesRequest
} from "./model/program-module-sequence";
import type { ApplySolutionRequest, ApplySolutionResponse, FeasibilityCheckResponse, SchedulingSolverRequest, SchedulingSolverResponse, SolveGroupsRequest } from './model/scheduling';
import type { Cohort, CreateCohortRequest, UpdateCohortRequest, UpcomingCohortResponse, CohortTransferRequest, UpcomingTransferRequest } from './model/cohort';
import type { CreateSiteRequest, Site, UpdateSiteRequest } from './model/site';
import type { InitializationStatusResponse } from './model/initialization-status';
import type { Student } from "./model/student";
import type { StudentAddress } from "./model/student-address";
import type { UserPreference } from './model/user-preference';

export interface DemonstrationConfigurationResponse {
    id: string;
    name: string;
    configurationJson: string;
}

export interface GeneratedPersonResponse {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
}

export interface ApplyConfigurationResult {
    success: boolean;
    errorMessage: string | null;
    studentsCreated: number;
    students: GeneratedPersonResponse[];
    teachersCreated: number;
    teachers: GeneratedPersonResponse[];
    programsActivated: number;
    activatedProgramNames: string[];
    groupsCreated: number;
    groups: { id: string; name: string; programName: string; startDate: string }[];
    skippedStarts: string[];
    presencesCreated: number;
    centerCreated: number;
    localsCreated: number;
    calendarsCreated: number;
    schedulingSolved: boolean | null;
    schedulingSolutionStats: Record<string, unknown> | null;
}

export interface CleanupResult {
    success: boolean;
    errorMessage: string | null;
    rowsDeleted: Record<string, number>;
}

export interface DataStatsResult {
    students: number;
    teachers: number;
    groups: number;
    programs: number;
    activePrograms: number;
    frequentations: number;
    presences: number;
    exams: number;
    sites: number;
    users: number;
    ateWorkPlacements: number;
}

function buildPagedParams(query: PagedGridQuery): URLSearchParams {
    const params = new URLSearchParams();
    params.set('page', String(query.page));
    params.set('pageSize', String(query.pageSize));
    if (query.search) params.set('search', query.search);
    if (query.sortBy) params.set('sortBy', query.sortBy);
    if (query.sortDesc) params.set('sortDesc', 'true');
    if (query.groupBy) params.set('groupBy', query.groupBy);
    if (query.jumpToLetter) params.set('jumpToLetter', query.jumpToLetter);
    if (query.filters) {
        for (const [key, value] of Object.entries(query.filters)) {
            if (value != null) params.set(key, value);
        }
    }
    return params;
}

export const getCustomSdk = () => {

    const sdk = useSdk();

    // LearningProgram API functions
    const learningPrograms = async () => sdk.fetchWrapper.get<LearningProgramsResponse>(`/learningPrograms/`);
    const availableLearningPrograms = async () => sdk.fetchWrapper.get<LearningProgramsResponse>(`/learningPrograms/available`);
    const getLearningProgram = async (id: string) => sdk.fetchWrapper.get<LearningProgramResponse>(`/learningPrograms/${id}`);
    const addLearningProgram = async (data: LearningProgramRequest) => await sdk.fetchWrapper.post<LearningProgramResponse>(`/learningPrograms/`, data);
    const updateLearningProgram = async (id: string, data: LearningProgramRequest) => await sdk.fetchWrapper.put<void>(`/learningPrograms/${id}`, data);
    const setLearningProgramAvailability = async (id: string, isAvailable: boolean) => await sdk.fetchWrapper.put<void>(`/learningPrograms/${id}/setAvailability`, { isAvailable });
    const cloneLearningProgram = async (id: string) => await sdk.fetchWrapper.post<LearningProgramResponse>(`/learningPrograms/${id}/clone`, {});

    const learningModules = async () => sdk.fetchWrapper.get<LearningModulesResponse>(`/learningModules/`);
    const learningModulesByProgram = async (programId: string) => sdk.fetchWrapper.get<LearningModulesResponse>(`/learningPrograms/${programId}/learningModules/`);
    const getLearningModule = async (id: string) => sdk.fetchWrapper.get<LearningModuleResponse>(`/learningModules/${id}`);
    const addLearningModule = async (data: LearningModuleRequest) => await sdk.fetchWrapper.post<LearningModuleResponse>(`/learningModules/`, data);
    const updateLearningModule = async (id: string, data: LearningModuleRequest) => await sdk.fetchWrapper.put<void>(`/learningModules/${id}`, data);
    const archiveLearningModule = async (id: string) => await sdk.fetchWrapper.put<void>(`/learningModules/${id}/archive`, {});

    // Teacher API functions
    const teachers = async () => sdk.fetchWrapper.get<TeachersResponse>(`/teachers/`);
    const teachersPaged = async (query: PagedGridQuery): Promise<PagedTeachersResponse> => {
        const params = buildPagedParams(query);
        return sdk.fetchWrapper.get<PagedTeachersResponse>(`/teachers/paged?${params.toString()}`);
    };
    const getCurrentTeacher = async () => sdk.fetchWrapper.get<{ teacher: TeacherResponse['teacher'] | null }>(`/teachers/current`);
    const getTeacherPlanning = async (teacherId: string) => {
        return sdk.fetchWrapper.get<import('./dto/teacher-planning-response').TeacherPlanningResponse>(`/teachers/planning?teacherId=${teacherId}`);
    };
    const getGroupPlanning = async (groupId: string) => {
        return sdk.fetchWrapper.get<import('./dto/teacher-planning-response').TeacherPlanningResponse>(`/teachers/planning?fallbackGroupId=${groupId}`);
    };
    const getTeachersCurrentActivity = async () =>
        sdk.fetchWrapper.get<import('./dto/teachers-activity-response').TeachersActivityResponse>(`/teachers/currentActivity`);
    const isTeacherGroupMappedToEntraId = async () => sdk.fetchWrapper.get<{ isMappedToEntraId: boolean }>(`/teachers/isTeacherGroupMappedToEntraId`);
    const teachersByProgram = async (programId: string) => sdk.fetchWrapper.get<TeachersResponse>(`/learningPrograms/${programId}/teachers/`);
    const addTeacher = async (userId: string) => await sdk.fetchWrapper.post<TeacherResponse>(`/teachers/`, { userId });
    const addTeachersFromEntraIdGroup = async (groupId: string) => await sdk.fetchWrapper.post<TeachersResponse>(`/teachers/FromEntraIdGroup`, { groupId });
    const archiveTeacher = async (id: string) => await sdk.fetchWrapper.put<void>(`/teachers/${id}/archive`, {});

    // Teacher-LearningProgram relationship functions
    const getTeacherLearningPrograms = async (teacherId: string) => sdk.fetchWrapper.get<LearningProgramsResponse>(`/teachers/${teacherId}/learningPrograms/`);
    const assignTeacherToProgram = async (teacherId: string, programId: string) => await sdk.fetchWrapper.post<void>(`/teachers/${teacherId}/learningPrograms/${programId}`, {});
    const removeTeacherFromProgram = async (teacherId: string, programId: string) => await sdk.fetchWrapper.delete<void>(`/teachers/${teacherId}/learningPrograms/${programId}`);

    // Teacher-LearningModule relationship functions (module affinity)
    const getTeacherLearningModules = async (teacherId: string) => sdk.fetchWrapper.get<LearningModulesResponse>(`/teachers/${teacherId}/learningModules/`);
    const setTeacherProgramModules = async (teacherId: string, programId: string, moduleIds: string[]) =>
        await sdk.fetchWrapper.put<void>(`/teachers/${teacherId}/learningPrograms/${programId}/modules`, { moduleIds });

    // Frequentation API functions
    const frequentations = async () => sdk.fetchWrapper.get<FrequentationsResponse>(`/frequentations/`);
    const getFrequentationById = async (id: string) => sdk.fetchWrapper.get<FrequentationResponse>(`/frequentations/${id}`);
    const frequentationsByTeacher = async (teacherId: string, date?: string) => {
        const params = date ? `?date=${date}` : '';
        return sdk.fetchWrapper.get<FrequentationsResponse>(`/teachers/${teacherId}/frequentations/${params}`);
    };
    const frequentationsByProgram = async (programId: string, options?: { excludeGroupMembers?: boolean }) => {
        const params = new URLSearchParams();
        params.append('excludeGroupMembers', (options?.excludeGroupMembers ?? false).toString());
        return sdk.fetchWrapper.get<FrequentationsResponse>(`/learningprograms/${programId}/frequentations/?${params.toString()}`);
    };
    const getStudentById = async (studentId: string) =>
        sdk.fetchWrapper.get<Student>(`/students/${studentId}`);
    const getStudentByUserId = async (userId: string) =>
        sdk.fetchWrapper.get<Student>(`/students/byUser/${userId}`);
    const frequentationsByStudent = async (studentId: string) =>
        sdk.fetchWrapper.get<FrequentationsResponse>(`/students/${studentId}/frequentations`);
    const addFrequentation = async (
        userId: string,
        learningProgramId: string,
        startDate: string,
        cohortId?: string | null,
        endDate: string | null = null,
        status: string | null = null,
        fundingSource: string | null = null,
        pathType: string | null = null,
        isConcomitant: boolean = false,
        nbHoursFG: number | null = null,
    ) => await sdk.fetchWrapper.post<FrequentationResponse>(`/frequentations/`, {
        userId, learningProgramId, startDate, cohortId, endDate, status, fundingSource, pathType, isConcomitant, nbHoursFG,
    });
    const addFrequentationsFromEntraIdGroup = async (groupId: string, learningProgramId: string, startDate: string, endDate: string | null) =>
        await sdk.fetchWrapper.post<{ importedCount: number; skippedCount: number }>(`/frequentations/FromEntraIdGroup`, { groupId, learningProgramId, startDate, endDate });
    const updateFrequentation = async (id: string, startDate: string, endDate: string | null, status: string, fundingSource: string | null, pathType: string | null, isConcomitant: boolean, nbHoursFG: number | null, isIndividual: boolean = false) => await sdk.fetchWrapper.put<FrequentationResponse>(`/frequentations/${id}`, { startDate, endDate, status, fundingSource, pathType, isConcomitant, nbHoursFG, isIndividual });

    const frequentationsPaged = async (query: PagedGridQuery): Promise<PagedFrequentationsResponse> => {
        const params = buildPagedParams(query);
        return sdk.fetchWrapper.get<PagedFrequentationsResponse>(`/frequentations/paged?${params.toString()}`);
    };

    // Student API functions
    const studentsPaged = async (query: PagedGridQuery): Promise<PagedStudentsResponse> => {
        const params = buildPagedParams(query);
        return sdk.fetchWrapper.get<PagedStudentsResponse>(`/students/paged?${params.toString()}`);
    };

    const addStudent = async (userId: string) =>
        await sdk.fetchWrapper.post<{ id: string; user: { id: string; email: string; firstName: string; lastName: string } }>(`/students/`, { userId });

    const updateStudentCoordinates = async (id: string, data: { permanentCode: string | null; recordNumber: string | null }) =>
        await sdk.fetchWrapper.put<void>(`/students/${id}/coordinates`, data);

    // Student Address functions
    const getStudentAddresses = async (studentId: string): Promise<StudentAddress[]> =>
        await sdk.fetchWrapper.get<StudentAddress[]>(`/students/${studentId}/addresses`);

    const createStudentAddress = async (studentId: string, data: Omit<StudentAddress, 'id' | 'studentId'>): Promise<StudentAddress> =>
        await sdk.fetchWrapper.post<StudentAddress>(`/students/${studentId}/addresses`, data);

    const updateStudentAddress = async (studentId: string, id: string, data: Omit<StudentAddress, 'id' | 'studentId'>): Promise<void> =>
        await sdk.fetchWrapper.put<void>(`/students/${studentId}/addresses/${id}`, data);

    const deleteStudentAddress = async (studentId: string, id: string): Promise<void> =>
        await sdk.fetchWrapper.delete<void>(`/students/${studentId}/addresses/${id}`);

    const searchStudentsWithFrequentation = async (
        query: string,
        options: { programId?: string; groupId?: string; excludeGroupId?: string; onlyWithActiveFrequentation?: boolean; maxResults?: number } = {}
    ): Promise<{ items: Student[] }> => {
        const params = new URLSearchParams({ query, maxResults: String(options.maxResults ?? 15) });
        if (options.programId) params.set('programId', options.programId);
        if (options.groupId) params.set('groupId', options.groupId);
        if (options.excludeGroupId) params.set('excludeGroupId', options.excludeGroupId);
        if (options.onlyWithActiveFrequentation) params.set('onlyWithActiveFrequentation', 'true');
        return sdk.fetchWrapper.get<{ items: Student[] }>(`/students/searchWithFrequentation?${params.toString()}`);
    };

    const searchStudents = async (query: string, maxResults = 15): Promise<{ items: Student[] }> => {
        const params = new URLSearchParams({ query, maxResults: String(maxResults) });
        return sdk.fetchWrapper.get<{ items: Student[] }>(`/students/search?${params.toString()}`);
    };

    // Presence functions
    const addPresence = async (data: PresenceRequest) =>
        await sdk.fetchWrapper.post<PresenceResponse>(`/presences/`, data);

    const updatePresence = async (id: string, data: Partial<Presence>) =>
        await sdk.fetchWrapper.put<void>(`/presences/${id}`, data);

    const deletePresence = async (id: string) =>
        await sdk.fetchWrapper.delete<void>(`/presences/${id}`);

    const bulkUpsertPresences = async (presences: Partial<Presence>[], presenceIdsToDelete: string[] = []) =>
        await sdk.fetchWrapper.post<{ createdCount: number; updatedCount: number; deletedCount: number }>(`/presences/bulk`, { presences, presenceIdsToDelete });

    const getPresencesByDateRange = async (startDate: string, endDate: string, programId?: string) => {
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        if (programId) params.append('programId', programId);
        return sdk.fetchWrapper.get<PresencesByStudentResponse>(`/presences/dateRange?${params.toString()}`);
    };

    const getTeacherPresencesByDateRange = async (teacherId: string, startDate: string, endDate: string) => {
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        return sdk.fetchWrapper.get<PresencesByStudentResponse>(`/teachers/${teacherId}/presences/dateRange?${params.toString()}`);
    };

    // FrequentationNote functions
    const getFrequentationNotesByPresence = async (presenceId: string) =>
        sdk.fetchWrapper.get<{ notes: FrequentationNote[] }>(`/presences/${presenceId}/notes`);

    const getFrequentationNotes = async (frequentationId: string) =>
        sdk.fetchWrapper.get<{ notes: FrequentationNote[] }>(`/frequentations/${frequentationId}/notes`);

    const createFrequentationNoteByPresence = async (presenceId: string, noteType: string, level: string, message: string) =>
        sdk.fetchWrapper.post<FrequentationNote>(`/presences/${presenceId}/notes`, { noteType, level, message });

    const createFrequentationNote = async (frequentationId: string, noteType: string, level: string, message: string) =>
        sdk.fetchWrapper.post<FrequentationNote>(`/frequentations/${frequentationId}/notes`, { noteType, level, message });

    const updateFrequentationNote = async (id: string, noteType: string, level: string, message: string) =>
        sdk.fetchWrapper.put<void>(`/frequentationNotes/${id}`, { noteType, level, message });

    const deleteFrequentationNote = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/frequentationNotes/${id}`);

    // Exam functions
    const exams = async () => sdk.fetchWrapper.get<ExamsResponse>(`/exams/`);

    const examsFiltered = async (filters: { programId?: string; groupId?: string; moduleId?: string; frequentationId?: string; teacherId?: string; dateFrom?: string; dateTo?: string; result?: string }) => {
        const params = new URLSearchParams();
        if (filters.programId) params.append('programId', filters.programId);
        if (filters.groupId) params.append('groupId', filters.groupId);
        if (filters.moduleId) params.append('moduleId', filters.moduleId);
        if (filters.frequentationId) params.append('frequentationId', filters.frequentationId);
        if (filters.teacherId) params.append('teacherId', filters.teacherId);
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);
        if (filters.result) params.append('result', filters.result);
        return sdk.fetchWrapper.get<{ exams: ExamListItem[] }>(`/exams/?${params.toString()}`);
    };

    const examsCount = async (filters: { programId?: string; groupId?: string; moduleId?: string; frequentationId?: string; teacherId?: string; dateFrom?: string; dateTo?: string; result?: string }) => {
        const params = new URLSearchParams();
        if (filters.programId) params.append('programId', filters.programId);
        if (filters.groupId) params.append('groupId', filters.groupId);
        if (filters.moduleId) params.append('moduleId', filters.moduleId);
        if (filters.frequentationId) params.append('frequentationId', filters.frequentationId);
        if (filters.teacherId) params.append('teacherId', filters.teacherId);
        if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters.dateTo) params.append('dateTo', filters.dateTo);
        if (filters.result) params.append('result', filters.result);
        return sdk.fetchWrapper.get<{ count: number }>(`/exams/count?${params.toString()}`);
    };

    const getExam = async (id: string) => sdk.fetchWrapper.get<ExamResponse>(`/exams/${id}`);

    const getExamsByFrequentation = async (frequentationId: string) =>
        sdk.fetchWrapper.get<ExamsResponse>(`/frequentations/${frequentationId}/exams`);

    const createExam = async (data: ExamRequest) =>
        await sdk.fetchWrapper.post<ExamResponse>(`/exams/`, data);

    const updateExam = async (id: string, data: Exam) =>
        await sdk.fetchWrapper.put<void>(`/exams/${id}`, data);

    const deleteExam = async (id: string) =>
        await sdk.fetchWrapper.delete<void>(`/exams/${id}`);

    // Schedule configuration now managed by Fondation Calendar system (sdk.Calendar)

    // Export/Import functions
    const exportLearningData = async (): Promise<Blob> => {
        return await sdk.fetchWrapper.download(`/learningData/export`);
    };

    const importLearningData = async (file: File): Promise<ImportResultDto> => {
        return await sdk.fetchWrapper.upload("/learningData/import", file) as unknown as ImportResultDto;
    };

    // Consolidated view function
    const getConsolidatedView = async (programId: string, includeInactive: boolean = false, teacherId?: string, excludeGroupMembers: boolean = false) => {
        const params = new URLSearchParams();
        params.append('programId', programId);
        params.append('includeInactive', includeInactive.toString());
        params.append('excludeGroupMembers', excludeGroupMembers.toString());
        if (teacherId) {
            params.append('teacherId', teacherId);
        }
        return sdk.fetchWrapper.get<ConsolidatedViewResponse>(`/consolidatedView/?${params.toString()}`);
    };

    // Cumulative hours function
    const getCumulativeHours = async (programId: string, includeInactive: boolean = false, teacherId?: string, excludeGroupMembers: boolean = false) => {
        const params = new URLSearchParams();
        params.append('programId', programId);
        params.append('includeInactive', includeInactive.toString());
        params.append('excludeGroupMembers', excludeGroupMembers.toString());
        if (teacherId) {
            params.append('teacherId', teacherId);
        }
        return sdk.fetchWrapper.get<CumulativeHoursResponse>(`/cumulativeHours/?${params.toString()}`);
    };

    // Exam presence report function
    const generateExamPresenceReport = async (startDate: string, endDate: string, programIds?: string[]): Promise<Blob> => {
        // Convert dates to ISO format for API
        const startDateISO = new Date(startDate).toISOString();
        const endDateISO = new Date(endDate).toISOString();

        const payload: ExamPresenceReportRequest = {
            startDate: startDateISO,
            endDate: endDateISO
        };

        if (programIds && programIds.length > 0) {
            payload.programIds = programIds;
        }

        return await sdk.fetchWrapper.downloadPost('/examPresenceReport/', payload);
    };

    // Absence report function
    const generateAbsenceReport = async (frequentationId: string, startDate: string, endDate: string): Promise<Blob> => {
        // Convert dates to ISO format for API
        const startDateISO = new Date(startDate).toISOString();
        const endDateISO = new Date(endDate).toISOString();

        const payload = {
            frequentationId,
            startDate: startDateISO,
            endDate: endDateISO
        };

        return await sdk.fetchWrapper.downloadPost('/absenceReport/', payload);
    };

    // Weekly presence report function
    const generateWeeklyPresenceReport = async (teacherId: string, startDate: string, endDate: string): Promise<Blob> => {
        // Convert dates to ISO format for API
        const startDateISO = new Date(startDate).toISOString();
        const endDateISO = new Date(endDate).toISOString();

        const payload = {
            teacherId,
            startDate: startDateISO,
            endDate: endDateISO
        };

        return await sdk.fetchWrapper.downloadPost('/weeklyPresenceReport/', payload);
    };

    const generateProgressionReport = async (frequentationId: string): Promise<Blob> => {
        return await sdk.fetchWrapper.downloadPost('/progressionReport/', { frequentationId });
    };

    const generateAtRiskReport = async (startDate: string, endDate: string, programId?: string, groupId?: string, teacherId?: string): Promise<Blob> => {
        const payload: Record<string, unknown> = {
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
        };
        if (programId) payload.programId = programId;
        if (groupId) payload.groupId = groupId;
        if (teacherId) payload.teacherId = teacherId;
        return await sdk.fetchWrapper.downloadPost('/atRiskReport/', payload);
    };

    const generateModulePerformanceReport = async (startDate: string, endDate: string, programId?: string, groupId?: string): Promise<Blob> => {
        const payload: Record<string, unknown> = {
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
        };
        if (programId) payload.programId = programId;
        if (groupId) payload.groupId = groupId;
        return await sdk.fetchWrapper.downloadPost('/modulePerformanceReport/', payload);
    };

    const generateTeacherWorkloadReport = async (startDate: string, endDate: string, programId?: string, groupId?: string): Promise<Blob> => {
        const payload: Record<string, unknown> = {
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
        };
        if (programId) payload.programId = programId;
        if (groupId) payload.groupId = groupId;
        return await sdk.fetchWrapper.downloadPost('/teacherWorkloadReport/', payload);
    };

    const generateAttendanceTrendReport = async (startDate: string, endDate: string, programId?: string, groupId?: string, teacherId?: string): Promise<Blob> => {
        const payload: Record<string, unknown> = {
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
        };
        if (programId) payload.programId = programId;
        if (groupId) payload.groupId = groupId;
        if (teacherId) payload.teacherId = teacherId;
        return await sdk.fetchWrapper.downloadPost('/attendanceTrendReport/', payload);
    };

    // Student journey function
    const getStudentJourney = async (userId: string) => {
        return sdk.fetchWrapper.get<StudentJourneyResponse>(`/studentJourney/${userId}`, { errorHandler: null });
    };

    // FrequentationModuleResults function
    const getFrequentationModuleResults = async (frequentationId: string) =>
        sdk.fetchWrapper.get<FrequentationModuleResultsResponse>(`/frequentations/${frequentationId}/moduleResults`);

    // FrequentationModuleStatus functions
    const getFrequentationModuleStatuses = async (frequentationId: string) =>
        sdk.fetchWrapper.get<{ statuses: Array<{ frequentationId: string; learningModuleId: string; status: string }> }>(`/frequentations/${frequentationId}/moduleStatuses`);

    const setModuleStatus = async (frequentationId: string, moduleId: string, status: string) =>
        await sdk.fetchWrapper.put<void>(`/frequentations/${frequentationId}/moduleStatuses/${moduleId}`, { status });

    const deleteModuleStatus = async (frequentationId: string, moduleId: string) =>
        await sdk.fetchWrapper.delete<void>(`/frequentations/${frequentationId}/moduleStatuses/${moduleId}`);

    // Module planning functions
    const getModulePlan = async (frequentationId: string, startDate: string, endDate: string) => {
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        params.append('endDate', endDate);
        return sdk.fetchWrapper.get<ModulePlanResponse>(`/frequentations/${frequentationId}/modulePlan?${params.toString()}`);
    };

    const getModulePlansBatch = async (frequentationIds: string[], startDate: string, endDate: string) =>
        sdk.fetchWrapper.post<ModulePlansBatchResponse>(`/modulePlans/batch`, { frequentationIds, startDate, endDate });

    /**
     * Returns the effective planning for a Planning aggregate (group or individual frequentation),
     * with PlanningExceptions baked in. Per-period entries are collapsed into contiguous segments.
     * When endDate is omitted, the server walks sequences until exhaustion (capped at a 50-year
     * safety horizon).
     */
    const getEffectivePlan = async (planningId: string, startDate: string, endDate?: string) => {
        const params = new URLSearchParams();
        params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        return sdk.fetchWrapper.get<EffectivePlanResponse>(`/plannings/${planningId}/effectivePlan?${params.toString()}`);
    };

    const getTeacherModulePlans = async (teacherId: string, date: string) =>
        sdk.fetchWrapper.get<ModulePlansBatchResponse>(`/teachers/${teacherId}/modulePlans?date=${date}`);

    const getTeacherDailyReport = async (teacherId: string, date: string, programId?: string) => {
        const params = new URLSearchParams();
        params.append('date', date);
        if (programId) params.append('programId', programId);
        return sdk.fetchWrapper.get<TeacherDailyReportResponse>(`/teachers/${teacherId}/dailyReport?${params.toString()}`);
    };

    // Director Dashboard functions
    const getStudentsWithRecentAbsences = async () => {
        return sdk.fetchWrapper.get<{ students: Array<{ studentId: string; user: { id: string; email: string; firstName: string; lastName: string }; absenceCount: number }> }>(`/DirectorDashboard/StudentsWithRecentAbsences`);
    };

    const getStudentsExceedingModuleTime = async () => {
        return sdk.fetchWrapper.get<{ students: Array<{ studentId: string; user: { id: string; email: string; firstName: string; lastName: string }; moduleNumber: string; moduleTitle: string; moduleCode: string; actualHours: number; expectedHours: number; percentageOfExpected: number }> }>(`/DirectorDashboard/StudentsExceedingModuleTime`);
    };

    const getStudentsWithDataIssues = async () => {
        return sdk.fetchWrapper.get<{
            groupMembershipIssues: Array<{ studentId: string; user: { id: string; email: string; firstName: string; lastName: string }; programTitle: string; groupCount: number }>;
            missingTeacherIssues: Array<{ studentId: string; user: { id: string; email: string; firstName: string; lastName: string }; programTitle: string }>;
        }>(`/DirectorDashboard/StudentsWithDataIssues`);
    };

    // Planning API functions
    const getPlannings = async () => sdk.fetchWrapper.get<PlanningsResponse>(`/plannings/`);
    const getPlanning = async (id: string) => sdk.fetchWrapper.get<PlanningResponse>(`/plannings/${id}`);
    const createPlanning = async (data: CreatePlanningRequest) => await sdk.fetchWrapper.post<PlanningResponse>(`/plannings/`, data);
    const updatePlanning = async (id: string, data: UpdatePlanningRequest) => await sdk.fetchWrapper.put<void>(`/plannings/${id}`, data);
    const deletePlanning = async (id: string) => await sdk.fetchWrapper.delete<void>(`/plannings/${id}`);

    // Group API functions
    const groups = async () => sdk.fetchWrapper.get<GroupsResponse>(`/groups/`);
    const getActiveGroups = async () => sdk.fetchWrapper.get<ActiveGroupsResponse>(`/groups/active`);
    const getGroup = async (id: string) => sdk.fetchWrapper.get<GroupResponse>(`/groups/${id}`);
    const createGroup = async (data: CreateGroupRequest) => await sdk.fetchWrapper.post<GroupResponse>(`/groups/`, data);
    const updateGroup = async (id: string, data: UpdateGroupRequest) => await sdk.fetchWrapper.put<void>(`/groups/${id}`, data);
    const deleteGroup = async (id: string) => await sdk.fetchWrapper.delete<void>(`/groups/${id}`);

    // Cohort API functions
    const getCohorts = async () => sdk.fetchWrapper.get<{ cohorts: Cohort[] }>(`/cohorts/`);
    const getCohort = async (id: string) => sdk.fetchWrapper.get<{ cohort: Cohort }>(`/cohorts/${id}`);
    const createCohort = async (data: CreateCohortRequest) => await sdk.fetchWrapper.post<{ cohort: Cohort }>(`/cohorts/`, data);
    const updateCohort = async (data: UpdateCohortRequest) => await sdk.fetchWrapper.put<void>(`/cohorts/`, data);
    const archiveCohort = async (id: string) => await sdk.fetchWrapper.put<void>(`/cohorts/${id}/archive`, {});
    const deleteCohort = async (id: string) => await sdk.fetchWrapper.delete<void>(`/cohorts/${id}`);
    const startCohort = async (id: string) => await sdk.fetchWrapper.post<void>(`/cohorts/${id}/start`, {});
    const transferCohort = async (id: string, data: CohortTransferRequest) =>
        await sdk.fetchWrapper.post<void>(`/cohorts/${id}/transfer`, data);
    const getUpcomingCohort = async () =>
        sdk.fetchWrapper.get<UpcomingCohortResponse>(`/cohorts/upcoming`);
    const transferFromUpcoming = async (data: UpcomingTransferRequest) =>
        await sdk.fetchWrapper.post<void>(`/cohorts/upcoming/transfer`, data);

    // Site (Centre) API functions
    const getSites = async () => sdk.fetchWrapper.get<{ sites: Site[] }>(`/sites/`);
    const getSite = async (id: string) => sdk.fetchWrapper.get<{ site: Site }>(`/sites/${id}`);
    const createSite = async (data: CreateSiteRequest) => await sdk.fetchWrapper.post<{ site: Site }>(`/sites/`, data);
    const updateSite = async (data: UpdateSiteRequest) => await sdk.fetchWrapper.put<void>(`/sites/`, data);
    const archiveSite = async (id: string) => await sdk.fetchWrapper.put<void>(`/sites/${id}/archive`, {});
    const setProgramSites = async (programId: string, siteIds: string[]) =>
        await sdk.fetchWrapper.put<void>(`/learningPrograms/${programId}/sites`, { siteIds });

    // Local API functions
    const getLocals = async () => sdk.fetchWrapper.get<{ locals: Local[] }>(`/locals/`);
    const getLocalsBySite = async (siteId: string) => sdk.fetchWrapper.get<{ locals: Local[] }>(`/sites/${siteId}/locals`);
    const getLocal = async (id: string) => sdk.fetchWrapper.get<{ local: Local }>(`/locals/${id}`);
    const createLocal = async (data: CreateLocalRequest) => await sdk.fetchWrapper.post<{ local: Local }>(`/locals/`, data);
    const updateLocal = async (data: UpdateLocalRequest) => await sdk.fetchWrapper.put<void>(`/locals/`, data);
    const archiveLocal = async (id: string) => await sdk.fetchWrapper.put<void>(`/locals/${id}/archive`, {});

    // Resource API functions
    const getResources = async () => sdk.fetchWrapper.get<{ resources: Resource[] }>(`/resources/`);
    const getResourcesBySite = async (siteId: string) => sdk.fetchWrapper.get<{ resources: Resource[] }>(`/sites/${siteId}/resources`);
    const getResource = async (id: string) => sdk.fetchWrapper.get<{ resource: Resource }>(`/resources/${id}`);
    const createResource = async (data: CreateResourceRequest) => await sdk.fetchWrapper.post<{ resource: Resource }>(`/resources/`, data);
    const updateResource = async (data: UpdateResourceRequest) => await sdk.fetchWrapper.put<void>(`/resources/`, data);
    const archiveResource = async (id: string) => await sdk.fetchWrapper.put<void>(`/resources/${id}/archive`, {});
    const setResourcePrograms = async (id: string, data: SetResourceProgramsRequest) => await sdk.fetchWrapper.put<void>(`/resources/${id}/programs`, data);

    // UserPreference API functions
    const getUserPreferences = async () => sdk.fetchWrapper.get<UserPreference>(`/userPreferences`);
    const updateUserPreferences = async (defaultSiteId: string | null) =>
        await sdk.fetchWrapper.put<void>(`/userPreferences`, { defaultSiteId });

    // FrequentationTag API functions
    const getFrequentationTags = async () => sdk.fetchWrapper.get<FrequentationTagsResponse>(`/frequentationTags`);
    const createFrequentationTag = async (data: CreateFrequentationTagRequest) =>
        await sdk.fetchWrapper.post<{ tag: FrequentationTag }>(`/frequentationTags`, data);
    const updateFrequentationTag = async (id: string, data: UpdateFrequentationTagRequest) =>
        await sdk.fetchWrapper.put<void>(`/frequentationTags/${id}`, data);
    const deleteFrequentationTag = async (id: string) =>
        await sdk.fetchWrapper.delete<void>(`/frequentationTags/${id}`);
    const addFrequentationTagValue = async (tagId: string, data: FrequentationTagValueRequest) =>
        await sdk.fetchWrapper.post<{ id: string; name: string }>(`/frequentationTags/${tagId}/values`, data);
    const updateFrequentationTagValue = async (tagId: string, valueId: string, data: FrequentationTagValueRequest) =>
        await sdk.fetchWrapper.put<void>(`/frequentationTags/${tagId}/values/${valueId}`, data);
    const deleteFrequentationTagValue = async (tagId: string, valueId: string) =>
        await sdk.fetchWrapper.delete<void>(`/frequentationTags/${tagId}/values/${valueId}`);
    const getTagsByFrequentation = async (frequentationId: string) =>
        sdk.fetchWrapper.get<FrequentationTagAssignmentsResponse>(`/frequentations/${frequentationId}/tags`);
    const setFrequentationTags = async (frequentationId: string, valueIds: string[]) =>
        await sdk.fetchWrapper.put<void>(`/frequentations/${frequentationId}/tags`, { valueIds });

    // FrequentationNoteTypeDefinition API functions
    const getFrequentationNoteTypeDefinitions = async () =>
        sdk.fetchWrapper.get<FrequentationNoteTypeDefinitionsResponse>(`/frequentationNoteTypeDefinitions`);
    const createFrequentationNoteTypeDefinition = async (data: CreateFrequentationNoteTypeDefinitionRequest) =>
        await sdk.fetchWrapper.post<FrequentationNoteTypeDefinition>(`/frequentationNoteTypeDefinitions`, data);
    const updateFrequentationNoteTypeDefinition = async (id: string, data: UpdateFrequentationNoteTypeDefinitionRequest) =>
        await sdk.fetchWrapper.put<void>(`/frequentationNoteTypeDefinitions/${id}`, data);
    const deleteFrequentationNoteTypeDefinition = async (id: string) =>
        await sdk.fetchWrapper.delete<void>(`/frequentationNoteTypeDefinitions/${id}`);
    const reorderFrequentationNoteTypeDefinitions = async (orderedIds: string[]) =>
        await sdk.fetchWrapper.put<void>(`/frequentationNoteTypeDefinitions/Reorder`, { orderedIds });

    // PresenceTypeDefinition API functions
    const getPresenceTypeDefinitions = async () =>
        sdk.fetchWrapper.get<PresenceTypeDefinitionsResponse>(`/presenceTypeDefinitions`);
    const createPresenceTypeDefinition = async (data: CreatePresenceTypeDefinitionRequest) =>
        await sdk.fetchWrapper.post<PresenceTypeDefinition>(`/presenceTypeDefinitions`, data);
    const updatePresenceTypeDefinition = async (id: string, data: UpdatePresenceTypeDefinitionRequest) =>
        await sdk.fetchWrapper.put<void>(`/presenceTypeDefinitions/${id}`, data);
    const deletePresenceTypeDefinition = async (id: string) =>
        await sdk.fetchWrapper.delete<void>(`/presenceTypeDefinitions/${id}`);
    const reorderPresenceTypeDefinitions = async (orderedIds: string[]) =>
        await sdk.fetchWrapper.put<void>(`/presenceTypeDefinitions/Reorder`, { orderedIds });

    // Group Membership API functions
    const getGroupMembers = async (groupId: string) => sdk.fetchWrapper.get<GroupMembersResponse>(`/groups/${groupId}/members`);
    const getAvailableGroupMembers = async (groupId: string) => sdk.fetchWrapper.get<FrequentationsResponse>(`/groups/${groupId}/availableMembers`);
    const addGroupMember = async (groupId: string, frequentationId: string) => await sdk.fetchWrapper.post<GroupMemberResponse>(`/groups/${groupId}/members`, { frequentationId });
    const removeGroupMember = async (groupId: string, frequentationId: string) => await sdk.fetchWrapper.delete<void>(`/groups/${groupId}/members/${frequentationId}`);

    // GroupModuleSequence API functions
    const getGroupModuleSequences = async (planningId: string) => sdk.fetchWrapper.get<GroupModuleSequencesResponse>(`/plannings/${planningId}/moduleSequences`);
    const updateGroupModuleSequences = async (planningId: string, data: UpdateGroupModuleSequencesRequest) => await sdk.fetchWrapper.put<void>(`/plannings/${planningId}/moduleSequences`, data);

    // ProgramModuleSequence API functions
    const getProgramModuleSequences = async (programId: string) => sdk.fetchWrapper.get<ProgramModuleSequencesResponse>(`/learningPrograms/${programId}/moduleSequences`);
    const updateProgramModuleSequences = async (programId: string, data: UpdateProgramModuleSequencesRequest) => await sdk.fetchWrapper.put<void>(`/learningPrograms/${programId}/moduleSequences`, data);

    // GroupModuleTeacher API functions
    const getGroupModuleTeachers = async (planningId: string) => sdk.fetchWrapper.get<GroupModuleTeachersResponse>(`/plannings/${planningId}/moduleTeachers`);
    const updateGroupModuleTeachers = async (planningId: string, data: UpdateGroupModuleTeachersRequest) => await sdk.fetchWrapper.put<void>(`/plannings/${planningId}/moduleTeachers`, data);
    const getProgramGroupMembers = async (programId: string) => sdk.fetchWrapper.get<GroupMembersByProgramResponse>(`/learningPrograms/${programId}/groupMembers`);
    const getProgramGroupModuleTeachers = async (programId: string) => sdk.fetchWrapper.get<GroupModuleTeachersByProgramResponse>(`/learningPrograms/${programId}/groupModuleTeachers`);

    // Group stats
    const getGroupStats = async (groupId: string) => sdk.fetchWrapper.get<GroupStatsResponse>(`/groups/${groupId}/stats`);
    const getGroupDashboardStats = async (groupId: string, date: string) => sdk.fetchWrapper.get<GroupDashboardStats>(`/groups/${groupId}/dashboardStats?date=${date}`);

    // PlanningException API functions
    const getPlanningExceptions = async (planningId: string) =>
        sdk.fetchWrapper.get<PlanningExceptionsResponse>(`/plannings/${planningId}/planningExceptions`);
    const createPlanningException = async (planningId: string, data: CreatePlanningExceptionRequest) =>
        await sdk.fetchWrapper.post<PlanningExceptionResponse>(`/plannings/${planningId}/planningExceptions`, data);
    const updatePlanningException = async (planningId: string, id: string, data: UpdatePlanningExceptionRequest) =>
        await sdk.fetchWrapper.put<PlanningExceptionResponse>(`/plannings/${planningId}/planningExceptions/${id}`, data);
    const deletePlanningException = async (planningId: string, id: string) =>
        await sdk.fetchWrapper.delete<void>(`/plannings/${planningId}/planningExceptions/${id}`);

    // Individual Planning API functions
    const createIndividualPlanning = async (frequentationId: string, data: { startDate?: string; calendarId?: string | null }) =>
        await sdk.fetchWrapper.post<{ planningId: string; frequentationId: string }>(`/frequentations/${frequentationId}/individualPlanning`, data);
    const deleteIndividualPlanning = async (frequentationId: string) =>
        await sdk.fetchWrapper.delete<void>(`/frequentations/${frequentationId}/individualPlanning`);
    const getIndividualPlanningsByProgram = async (programId: string) =>
        sdk.fetchWrapper.get<FrequentationsResponse>(`/learningPrograms/${programId}/individualPlannings`);

    const MODULE_NAME = "Parcours.LearningModule";
    const ROLE_ADMINISTRATOR = MODULE_NAME + ".Administrateur";
    const ROLE_SUPERVISOR = MODULE_NAME + ".Superviseur";
    const ROLE_TEACHER = MODULE_NAME + ".Enseignant";
    const ROLE_VIEWER = MODULE_NAME + ".Visiteur";

    // ================================================================
    // ATE (Alternance Travail-Études) API functions
    // ================================================================

    const ATE_MODULE = "Parcours.ATE";
    const ATE_ROLE_ADMINISTRATOR = ATE_MODULE + ".Administrateur";
    const ATE_ROLE_TEACHER = ATE_MODULE + ".Enseignant";
    const ATE_ROLE_SUPERVISOR = ATE_MODULE + ".Superviseur";
    const ATE_ROLE_VIEWER = ATE_MODULE + ".Visiteur";

    // ================================================================
    // Demonstration API functions
    // ================================================================

    const DEMO_MODULE = "Parcours.Demonstration";
    const DEMO_ROLE_ADMINISTRATOR = DEMO_MODULE + ".Administrateur";

    const getDemonstrationConfigurations = async () =>
        sdk.fetchWrapper.get<DemonstrationConfigurationResponse[]>(`/demonstration/configurations`);

    const getDemonstrationConfiguration = async (id: string) =>
        sdk.fetchWrapper.get<DemonstrationConfigurationResponse>(`/demonstration/configurations/${id}`);

    const createDemonstrationConfiguration = async (name: string, configurationJson: string) =>
        sdk.fetchWrapper.post<DemonstrationConfigurationResponse>(`/demonstration/configurations`, { name, configurationJson });

    const updateDemonstrationConfiguration = async (id: string, name: string, configurationJson: string) =>
        sdk.fetchWrapper.put<DemonstrationConfigurationResponse>(`/demonstration/configurations/${id}`, { name, configurationJson });

    const deleteDemonstrationConfiguration = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/demonstration/configurations/${id}`);

    const generateDemoStudents = async (count: number) =>
        sdk.fetchWrapper.post<GeneratedPersonResponse[]>(`/demonstration/generate-students`, { count });

    const generateDemoTeachers = async (count: number) =>
        sdk.fetchWrapper.post<GeneratedPersonResponse[]>(`/demonstration/generate-teachers`, { count });

    const applyDemonstrationConfiguration = async (id: string) =>
        sdk.fetchWrapper.post<ApplyConfigurationResult>(`/demonstration/configurations/${id}/apply`, {});

    /**
     * SSE streaming variant of applyDemonstrationConfiguration.
     * Returns the raw Response so the caller can read SSE events as a stream.
     */
    const applyDemonstrationConfigStream = async (id: string): Promise<Response> => {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        const init: RequestInit = { method: 'POST', headers, body: '{}' };
        if (isOidcBasedProvider(sdk.selectedAuthProvider.value)) {
            init.credentials = 'include';
        } else if (!sdk.isAnonymous.value) {
            if (sdk.isTokenAuth.value) {
                headers['Authorization'] = `Token ${sdk.apiToken.value}`;
            } else {
                const token = await sdk.getApiAccessToken();
                if (token) headers['Authorization'] = `Bearer ${token}`;
            }
        }
        const apiUrl = sdk.settings.value?.apiUrl ?? '';
        return fetch(`${apiUrl}/demonstration/configurations/${id}/apply-stream`, init);
    };

    const cleanupDemoData = async () =>
        sdk.fetchWrapper.post<CleanupResult>(`/demonstration/cleanup`, {});

    const getDemoDataStats = async () =>
        sdk.fetchWrapper.get<DataStatsResult>(`/demonstration/stats`);

    // Reference Data
    const getAteRatingGroups = async () =>
        sdk.fetchWrapper.get<AteAssessmentRatingGroup[]>(`/ate/ratinggroups`);
    const createAteRatingGroup = async (data: Partial<AteAssessmentRatingGroup>) =>
        sdk.fetchWrapper.post<AteAssessmentRatingGroup>(`/ate/ratinggroups`, data);
    const updateAteRatingGroup = async (id: string, data: Partial<AteAssessmentRatingGroup>) =>
        sdk.fetchWrapper.put<void>(`/ate/ratinggroups/${id}`, data);
    const archiveAteRatingGroup = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/ratinggroups/${id}`);

    const getAteActivityTemplates = async (moduleId: string) =>
        sdk.fetchWrapper.get<AteActivityTemplate[]>(`/ate/learningmodules/${moduleId}/activitytemplates`);
    const createAteActivityTemplate = async (data: Partial<AteActivityTemplate>) =>
        sdk.fetchWrapper.post<AteActivityTemplate>(`/ate/activitytemplates`, data);
    const updateAteActivityTemplate = async (id: string, data: Partial<AteActivityTemplate>) =>
        sdk.fetchWrapper.put<void>(`/ate/activitytemplates/${id}`, data);
    const archiveAteActivityTemplate = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/activitytemplates/${id}`);

    const getAteQuestionnaireTemplates = async (programId: string) =>
        sdk.fetchWrapper.get<AteQuestionnaireTemplate[]>(`/ate/learningprograms/${programId}/questionnairetemplates`);
    const createAteQuestionnaireTemplate = async (data: Partial<AteQuestionnaireTemplate>) =>
        sdk.fetchWrapper.post<AteQuestionnaireTemplate>(`/ate/questionnairetemplates`, data);
    const updateAteQuestionnaireTemplate = async (id: string, data: Partial<AteQuestionnaireTemplate>) =>
        sdk.fetchWrapper.put<void>(`/ate/questionnairetemplates/${id}`, data);
    const archiveAteQuestionnaireTemplate = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/questionnairetemplates/${id}`);
    const getArchivedAteQuestionnaireTemplates = async (programId: string) =>
        sdk.fetchWrapper.get<AteQuestionnaireTemplate[]>(`/ate/learningprograms/${programId}/questionnairetemplates/archived`);
    const restoreAteQuestionnaireTemplate = async (id: string) =>
        sdk.fetchWrapper.post<void>(`/ate/questionnairetemplates/${id}/restore`, {});
    const createAteQuestionnaireTemplateItem = async (templateId: string, data: Partial<AteQuestionnaireItemTemplate>) =>
        sdk.fetchWrapper.post<AteQuestionnaireItemTemplate>(`/ate/questionnairetemplates/${templateId}/items`, data);
    const updateAteQuestionnaireTemplateItem = async (templateId: string, itemId: string, data: Partial<AteQuestionnaireItemTemplate>) =>
        sdk.fetchWrapper.put<void>(`/ate/questionnairetemplates/${templateId}/items/${itemId}`, data);
    const deleteAteQuestionnaireTemplateItem = async (templateId: string, itemId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/questionnairetemplates/${templateId}/items/${itemId}`);

    const getAteHACTemplates = async (programId: string) =>
        sdk.fetchWrapper.get<AteHACTemplate[]>(`/ate/learningprograms/${programId}/hactemplates`);
    const createAteHACTemplate = async (data: Partial<AteHACTemplate>) =>
        sdk.fetchWrapper.post<AteHACTemplate>(`/ate/hactemplates`, data);
    const updateAteHACTemplate = async (id: string, data: Partial<AteHACTemplate>) =>
        sdk.fetchWrapper.put<void>(`/ate/hactemplates/${id}`, data);
    const archiveAteHACTemplate = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/hactemplates/${id}`);

    // Enterprises
    const getAteEnterprises = async () =>
        sdk.fetchWrapper.get<AteEnterprise[]>(`/ate/enterprises`);
    const getAteEnterprise = async (id: string) =>
        sdk.fetchWrapper.get<AteEnterprise>(`/ate/enterprises/${id}`);
    const createAteEnterprise = async (data: Partial<AteEnterprise>) =>
        sdk.fetchWrapper.post<AteEnterprise>(`/ate/enterprises`, data);
    const updateAteEnterprise = async (id: string, data: Partial<AteEnterprise>) =>
        sdk.fetchWrapper.put<void>(`/ate/enterprises/${id}`, data);
    const archiveAteEnterprise = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/enterprises/${id}`);

    // Enterprise Contacts
    const getAteEnterpriseContacts = async (enterpriseId: string) =>
        sdk.fetchWrapper.get<AteEnterpriseContact[]>(`/ate/enterprises/${enterpriseId}/contacts`);
    const getAteEnterpriseContact = async (id: string) =>
        sdk.fetchWrapper.get<AteEnterpriseContact>(`/ate/contacts/${id}`);
    const createAteEnterpriseContact = async (enterpriseId: string, data: Partial<AteEnterpriseContact>) =>
        sdk.fetchWrapper.post<AteEnterpriseContact>(`/ate/enterprises/${enterpriseId}/contacts`, data);
    const updateAteEnterpriseContact = async (id: string, data: Partial<AteEnterpriseContact>) =>
        sdk.fetchWrapper.put<void>(`/ate/contacts/${id}`, data);
    const archiveAteEnterpriseContact = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/contacts/${id}`);

    // Work Placements
    const getAteWorkPlacement = async (id: string) =>
        sdk.fetchWrapper.get<AteWorkPlacement>(`/ate/workplacements/${id}`);
    const getAteWorkPlacementsByFrequentation = async (frequentationId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacement[]>(`/ate/frequentations/${frequentationId}/workplacements`);
    const getAteWorkPlacementsByGroup = async (groupId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacement[]>(`/ate/groups/${groupId}/workplacements`);
    const createAteWorkPlacement = async (workPlacement: Partial<AteWorkPlacement>, moduleIds: string[]) =>
        sdk.fetchWrapper.post<AteWorkPlacement>(`/ate/workplacements`, { workPlacement, moduleIds });
    const updateAteWorkPlacement = async (id: string, data: Partial<AteWorkPlacement>) =>
        sdk.fetchWrapper.put<void>(`/ate/workplacements/${id}`, data);
    const archiveAteWorkPlacement = async (id: string) =>
        sdk.fetchWrapper.put<void>(`/ate/workplacements/${id}/archive`, {});
    const assignAteContactToWorkPlacement = async (id: string, contactId: string) =>
        sdk.fetchWrapper.post<void>(`/ate/workplacements/${id}/contacts/${contactId}`, {});
    const removeAteContactFromWorkPlacement = async (id: string, contactId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/workplacements/${id}/contacts/${contactId}`);

    // Work Placement Notes
    const getAteWorkPlacementNotes = async (workPlacementId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementNote[]>(`/ate/workplacements/${workPlacementId}/notes`);
    const createAteWorkPlacementNote = async (workPlacementId: string, data: Partial<AteWorkPlacementNote>) =>
        sdk.fetchWrapper.post<AteWorkPlacementNote>(`/ate/workplacements/${workPlacementId}/notes`, data);
    const updateAteWorkPlacementNote = async (id: string, data: Partial<AteWorkPlacementNote>) =>
        sdk.fetchWrapper.put<void>(`/ate/notes/${id}`, data);
    const deleteAteWorkPlacementNote = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/notes/${id}`);

    // Journal (Work Placement Activities)
    const getAteWorkPlacementActivities = async (workPlacementId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementActivity[]>(`/ate/workplacements/${workPlacementId}/activities`);
    const getAteWorkPlacementActivity = async (id: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementActivity>(`/ate/activities/${id}`);
    const createAteWorkPlacementActivity = async (workPlacementId: string, data: Partial<AteWorkPlacementActivity>) =>
        sdk.fetchWrapper.post<AteWorkPlacementActivity>(`/ate/workplacements/${workPlacementId}/activities`, data);
    const updateAteWorkPlacementActivity = async (id: string, data: Partial<AteWorkPlacementActivity>) =>
        sdk.fetchWrapper.put<void>(`/ate/activities/${id}`, data);
    const deleteAteWorkPlacementActivity = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/activities/${id}`);
    const addAteActivityNote = async (activityId: string, data: Partial<AteWorkPlacementActivityNote>) =>
        sdk.fetchWrapper.post<AteWorkPlacementActivityNote>(`/ate/activities/${activityId}/notes`, data);
    const deleteAteActivityNote = async (noteId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/activitynotes/${noteId}`);

    // Assessments
    const getAteActivityAssessments = async (activityId: string) =>
        sdk.fetchWrapper.get<AteActivityAssessment[]>(`/ate/activities/${activityId}/assessments`);
    const createAteActivityAssessment = async (activityId: string, data: Partial<AteActivityAssessment>) =>
        sdk.fetchWrapper.post<AteActivityAssessment>(`/ate/activities/${activityId}/assessments`, data);
    const updateAteActivityAssessment = async (id: string, data: Partial<AteActivityAssessment>) =>
        sdk.fetchWrapper.put<void>(`/ate/assessments/${id}`, data);
    const deleteAteActivityAssessment = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/assessments/${id}`);

    // Visits
    const getAteVisits = async (workPlacementId: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementVisit[]>(`/ate/workplacements/${workPlacementId}/visits`);
    const getAteVisit = async (id: string) =>
        sdk.fetchWrapper.get<AteWorkPlacementVisit>(`/ate/visits/${id}`);
    const createAteVisit = async (workPlacementId: string, data: Partial<AteWorkPlacementVisit>) =>
        sdk.fetchWrapper.post<AteWorkPlacementVisit>(`/ate/workplacements/${workPlacementId}/visits`, data);
    const updateAteVisit = async (id: string, data: Partial<AteWorkPlacementVisit>) =>
        sdk.fetchWrapper.put<void>(`/ate/visits/${id}`, data);
    const deleteAteVisit = async (id: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/visits/${id}`);
    const addAteVisitNote = async (visitId: string, data: Partial<AteWorkPlacementVisitNote>) =>
        sdk.fetchWrapper.post<AteWorkPlacementVisitNote>(`/ate/visits/${visitId}/notes`, data);
    const deleteAteVisitNote = async (noteId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/visitnotes/${noteId}`);

    // Questionnaires
    const getAteQuestionnaires = async (workPlacementId: string) =>
        sdk.fetchWrapper.get<AteQuestionnaire[]>(`/ate/workplacements/${workPlacementId}/questionnaires`);
    const getAteQuestionnaire = async (id: string) =>
        sdk.fetchWrapper.get<AteQuestionnaire>(`/ate/questionnaires/${id}`);
    const generateAteQuestionnaire = async (workPlacementId: string, templateId: string, visitId?: string | null, contactId?: string | null) =>
        sdk.fetchWrapper.post<AteQuestionnaire>(`/ate/workplacements/${workPlacementId}/questionnaires/generate`, { templateId, visitId, contactId });
    const sendAteQuestionnaire = async (id: string) =>
        sdk.fetchWrapper.post<void>(`/ate/questionnaires/${id}/send`, {});
    const respondToAteQuestionnaireItem = async (itemId: string, content: string) =>
        sdk.fetchWrapper.post<AteQuestionnaireItem>(`/ate/questionnaireitems/${itemId}/respond`, { content });
    const completeAteQuestionnaire = async (id: string) =>
        sdk.fetchWrapper.post<void>(`/ate/questionnaires/${id}/complete`, {});

    // HAC
    const getAteHACs = async (workPlacementId: string) =>
        sdk.fetchWrapper.get<AteHAC[]>(`/ate/workplacements/${workPlacementId}/hacs`);
    const getAteHAC = async (id: string) =>
        sdk.fetchWrapper.get<AteHAC>(`/ate/hacs/${id}`);
    const createAteHAC = async (workPlacementId: string, templateId: string) =>
        sdk.fetchWrapper.post<AteHAC>(`/ate/workplacements/${workPlacementId}/hacs`, { templateId });
    const upsertAteHACEntry = async (hacId: string, data: Partial<AteHACEntry>) =>
        sdk.fetchWrapper.post<AteHACEntry>(`/ate/hacs/${hacId}/entries`, data);
    const deleteAteHACEntry = async (entryId: string) =>
        sdk.fetchWrapper.delete<void>(`/ate/hac-entries/${entryId}`);

    // Scheduling / Simulation
    const solveScheduling = async (data: SchedulingSolverRequest) =>
        sdk.fetchWrapper.post<SchedulingSolverResponse>(`/scheduling/solve`, data);
    const solveGroupScheduling = async (data: SolveGroupsRequest) =>
        sdk.fetchWrapper.post<SchedulingSolverResponse>(`/scheduling/solve-groups`, data);
    const canSolveGroupScheduling = async (data: SolveGroupsRequest) =>
        sdk.fetchWrapper.post<FeasibilityCheckResponse>(`/scheduling/can-solve-groups`, data);
    const applySolution = async (data: ApplySolutionRequest) =>
        sdk.fetchWrapper.post<ApplySolutionResponse>(`/scheduling/apply-solution`, data);

    /**
     * Calls the SSE streaming endpoint and returns the raw Response so the caller
     * can read the body as a stream.  Auth headers are replicated from the SDK.
     */
    const solveGroupSchedulingStream = async (data: SolveGroupsRequest): Promise<Response> => {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        const init: RequestInit = { method: 'POST', headers, body: JSON.stringify(data) };
        if (isOidcBasedProvider(sdk.selectedAuthProvider.value)) {
            init.credentials = 'include';
        } else if (!sdk.isAnonymous.value) {
            if (sdk.isTokenAuth.value) {
                headers['Authorization'] = `Token ${sdk.apiToken.value}`;
            } else {
                const token = await sdk.getApiAccessToken();
                if (token) headers['Authorization'] = `Bearer ${token}`;
            }
        }
        const apiUrl = sdk.settings.value?.apiUrl ?? '';
        return fetch(`${apiUrl}/scheduling/solve-groups-stream`, init);
    };

    // Initialization status
    const getInitializationStatus = async () => sdk.fetchWrapper.get<InitializationStatusResponse>(`/initializationStatus`);

    return {
        learningPrograms,
        availableLearningPrograms,
        getLearningProgram,
        addLearningProgram,
        updateLearningProgram,
        setLearningProgramAvailability,
        cloneLearningProgram,
        learningModules,
        learningModulesByProgram,
        getLearningModule,
        addLearningModule,
        updateLearningModule,
        archiveLearningModule,
        exportLearningData,
        importLearningData,
        teachers,
        teachersPaged,
        getCurrentTeacher,
        getTeacherPlanning,
        getTeachersCurrentActivity,
        getGroupPlanning,
        isTeacherGroupMappedToEntraId,
        teachersByProgram,
        addTeacher,
        addTeachersFromEntraIdGroup,
        archiveTeacher,
        getTeacherLearningPrograms,
        assignTeacherToProgram,
        removeTeacherFromProgram,
        getTeacherLearningModules,
        setTeacherProgramModules,
        frequentations,
        getFrequentationById,
        frequentationsByTeacher,
        frequentationsByProgram,
        getStudentById,
        getStudentByUserId,
        frequentationsByStudent,
        addFrequentation,
        addFrequentationsFromEntraIdGroup,
        updateFrequentation,
        frequentationsPaged,
        updateStudentCoordinates,
        getStudentAddresses,
        createStudentAddress,
        updateStudentAddress,
        deleteStudentAddress,
        searchStudentsWithFrequentation,
        searchStudents,
        studentsPaged,
        addStudent,
        addPresence,
        updatePresence,
        deletePresence,
        bulkUpsertPresences,
        getPresencesByDateRange,
        getTeacherPresencesByDateRange,
        getFrequentationNotesByPresence,
        getFrequentationNotes,
        createFrequentationNoteByPresence,
        createFrequentationNote,
        updateFrequentationNote,
        deleteFrequentationNote,
        exams,
        examsFiltered,
        examsCount,
        getExam,
        getExamsByFrequentation,
        createExam,
        updateExam,
        deleteExam,
        getConsolidatedView,
        getCumulativeHours,
        generateExamPresenceReport,
        generateAbsenceReport,
        generateWeeklyPresenceReport,
        generateProgressionReport,
        generateAtRiskReport,
        generateModulePerformanceReport,
        generateTeacherWorkloadReport,
        generateAttendanceTrendReport,
        getStudentJourney,
        getFrequentationModuleResults,
        getFrequentationModuleStatuses,
        setModuleStatus,
        deleteModuleStatus,
        getModulePlan,
        getModulePlansBatch,
        getEffectivePlan,
        getTeacherModulePlans,
        getTeacherDailyReport,
        getStudentsWithRecentAbsences,
        getStudentsExceedingModuleTime,
        getStudentsWithDataIssues,
        getPlannings,
        getPlanning,
        createPlanning,
        updatePlanning,
        deletePlanning,
        groups,
        getActiveGroups,
        getGroup,
        createGroup,
        updateGroup,
        deleteGroup,
        // Cohort functions
        getCohorts,
        getCohort,
        createCohort,
        updateCohort,
        archiveCohort,
        deleteCohort,
        startCohort,
        transferCohort,
        getUpcomingCohort,
        transferFromUpcoming,
        // Site (Centre) functions
        getSites,
        getSite,
        createSite,
        updateSite,
        archiveSite,
        setProgramSites,
        // Local functions
        getLocals,
        getLocalsBySite,
        getLocal,
        createLocal,
        updateLocal,
        archiveLocal,
        // Resource functions
        getResources,
        getResourcesBySite,
        getResource,
        createResource,
        updateResource,
        archiveResource,
        setResourcePrograms,
                // UserPreference functions
        getUserPreferences,
        updateUserPreferences,
        // FrequentationTag functions
        getFrequentationTags,
        createFrequentationTag,
        updateFrequentationTag,
        deleteFrequentationTag,
        addFrequentationTagValue,
        updateFrequentationTagValue,
        deleteFrequentationTagValue,
        getTagsByFrequentation,
        setFrequentationTags,
        // FrequentationNoteTypeDefinition functions
        getFrequentationNoteTypeDefinitions,
        createFrequentationNoteTypeDefinition,
        updateFrequentationNoteTypeDefinition,
        deleteFrequentationNoteTypeDefinition,
        reorderFrequentationNoteTypeDefinitions,
        // PresenceTypeDefinition functions
        getPresenceTypeDefinitions,
        createPresenceTypeDefinition,
        updatePresenceTypeDefinition,
        deletePresenceTypeDefinition,
        reorderPresenceTypeDefinitions,
        getGroupMembers,
        getAvailableGroupMembers,
        addGroupMember,
        removeGroupMember,
        getProgramModuleSequences,
        updateProgramModuleSequences,
        getGroupModuleTeachers,
        updateGroupModuleTeachers,
        getProgramGroupMembers,
        getProgramGroupModuleTeachers,
        getGroupModuleSequences,
        updateGroupModuleSequences,
        getGroupStats,
        getGroupDashboardStats,
        getPlanningExceptions,
        createPlanningException,
        updatePlanningException,
        deletePlanningException,
        // Individual Planning
        createIndividualPlanning,
        deleteIndividualPlanning,
        getIndividualPlanningsByProgram,
        // ATE
        ATE_MODULE,
        ATE_ROLE_ADMINISTRATOR,
        ATE_ROLE_TEACHER,
        ATE_ROLE_SUPERVISOR,
        ATE_ROLE_VIEWER,
        getAteRatingGroups,
        createAteRatingGroup,
        updateAteRatingGroup,
        archiveAteRatingGroup,
        getAteActivityTemplates,
        createAteActivityTemplate,
        updateAteActivityTemplate,
        archiveAteActivityTemplate,
        getAteQuestionnaireTemplates,
        createAteQuestionnaireTemplate,
        updateAteQuestionnaireTemplate,
        archiveAteQuestionnaireTemplate,
        getArchivedAteQuestionnaireTemplates,
        restoreAteQuestionnaireTemplate,
        createAteQuestionnaireTemplateItem,
        updateAteQuestionnaireTemplateItem,
        deleteAteQuestionnaireTemplateItem,
        getAteHACTemplates,
        createAteHACTemplate,
        updateAteHACTemplate,
        archiveAteHACTemplate,
        getAteEnterprises,
        getAteEnterprise,
        createAteEnterprise,
        updateAteEnterprise,
        archiveAteEnterprise,
        getAteEnterpriseContacts,
        getAteEnterpriseContact,
        createAteEnterpriseContact,
        updateAteEnterpriseContact,
        archiveAteEnterpriseContact,
        getAteWorkPlacement,
        getAteWorkPlacementsByFrequentation,
        getAteWorkPlacementsByGroup,
        createAteWorkPlacement,
        updateAteWorkPlacement,
        archiveAteWorkPlacement,
        assignAteContactToWorkPlacement,
        removeAteContactFromWorkPlacement,
        getAteWorkPlacementNotes,
        createAteWorkPlacementNote,
        updateAteWorkPlacementNote,
        deleteAteWorkPlacementNote,
        getAteWorkPlacementActivities,
        getAteWorkPlacementActivity,
        createAteWorkPlacementActivity,
        updateAteWorkPlacementActivity,
        deleteAteWorkPlacementActivity,
        addAteActivityNote,
        deleteAteActivityNote,
        getAteActivityAssessments,
        createAteActivityAssessment,
        updateAteActivityAssessment,
        deleteAteActivityAssessment,
        getAteVisits,
        getAteVisit,
        createAteVisit,
        updateAteVisit,
        deleteAteVisit,
        addAteVisitNote,
        deleteAteVisitNote,
        getAteQuestionnaires,
        getAteQuestionnaire,
        generateAteQuestionnaire,
        sendAteQuestionnaire,
        respondToAteQuestionnaireItem,
        completeAteQuestionnaire,
        getAteHACs,
        getAteHAC,
        createAteHAC,
        upsertAteHACEntry,
        deleteAteHACEntry,
        ROLE_ADMINISTRATOR,
        ROLE_SUPERVISOR,
        ROLE_TEACHER,
        ROLE_VIEWER,
        calendarSdk: sdk.Calendar,
        // Demonstration
        DEMO_MODULE,
        DEMO_ROLE_ADMINISTRATOR,
        getDemonstrationConfigurations,
        getDemonstrationConfiguration,
        createDemonstrationConfiguration,
        updateDemonstrationConfiguration,
        deleteDemonstrationConfiguration,
        generateDemoStudents,
        generateDemoTeachers,
        applyDemonstrationConfiguration,
        applyDemonstrationConfigStream,
        cleanupDemoData,
        getDemoDataStats,
        // Scheduling / Simulation
        solveScheduling,
        solveGroupScheduling,
        canSolveGroupScheduling,
        solveGroupSchedulingStream,
        applySolution,
        // Initialization
        getInitializationStatus,
    }
}
